import { IdentityType, ProfileResource, RelationshipStatus } from '@foxbun/lib-goferal-types';
import { computed, observable } from 'mobx';
import { AbstractModelOptions } from '../stores/models/abstract/AbstractModelStore';
import { generatePath } from 'react-router-dom';
import { PackModel } from './PackModel';
import { PackStore } from '../stores/models/PackStore';
import { Routing } from '../../pages/Routing';
import { GroupStore } from '../stores/models/GroupStore';
import { GroupModel } from './GroupModel';
import { ObjectId } from 'bson';
import { ImageModel } from './ImageModel';
import { ResourceModel } from './ResourceModel';
import { ProfileImageModel } from './ProfileImageModel';
import { ImageModelData } from './ImageModel';

export interface ProfileModelOptions<T extends ProfileResource> extends AbstractModelOptions<T> {
  pack_store: PackStore;
  group_store: GroupStore;
}

export const PUP_RELATIONSHIP_STATUS = {
  [RelationshipStatus.IN_RELATIONSHIP]: 'Collared',
  [RelationshipStatus.OPEN_RELATIONSHIP]: 'Allowed to roam',
  [RelationshipStatus.SINGLE]: 'Lone wolf',
  [RelationshipStatus.DATING]: 'Tail sniffer'
};

export class ProfileModel<T extends ProfileResource = ProfileResource> extends ResourceModel<T> {
  @observable
  accessor gallery_images: ImageModel[];

  @observable
  accessor packs: PackModel[];

  @observable
  accessor groups: GroupModel[];

  constructor(protected options2: ProfileModelOptions<T>) {
    super(options2);
    this.gallery_images = [];
    this.packs = [];
    this.groups = [];
  }

  @computed get relationshipStatus() {
    if (this.data.identity.includes(IdentityType.PUP)) {
      if (PUP_RELATIONSHIP_STATUS[this.data.relationship_status]) {
        return PUP_RELATIONSHIP_STATUS[this.data.relationship_status];
      }
    }
    return this.data.relationship_status;
  }

  get isMe() {
    return false;
  }

  get link() {
    return generatePath(Routing.PROFILE, {
      id: this.id.toString()
    });
  }

  protected createImageModel(data: ImageModelData): ImageModel {
    return new ProfileImageModel(data, this);
  }

  setData(data: T) {
    super.setData(data);
  }

  async refresh() {
    this.setData(
      (await this.options.api_client.profile.getProfile({
        _id: this.id
      })) as T
    );
  }

  async loadGalleryImages() {
    const result = await this.options.api_client.profile.getProfileGalleryImages({
      _id: this.id
    });
    this.gallery_images = result.items.map((image) => new ProfileImageModel(image, this));
  }

  async deleteGalleryImage(image_id: ObjectId) {
    await this.options.api_client.profile.deleteProfileGalleryImage({
      _id: image_id
    });
    await this.loadGalleryImages();
    await this.refresh();
  }

  async loadPacks() {
    this.packs = await this.options2.pack_store.getProfilePacks(this.id);
  }

  async loadGroups() {
    this.groups = await this.options2.group_store.getProfileGroups(this.id);
  }
}
